import styled, { css } from 'styled-components';
import { media } from '../../utils/media';
import { theme } from '../common/theme';

export const Card = styled.div`
  max-width: 900px;
  margin: ${({ theme }) => theme.spacing.xl} auto;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Mobile adjustments */
  @media (max-width: 640px) {
    margin: 0;
    padding: ${({ theme }) => theme.spacing.md};
    border-radius: 0;
    min-height: 100vh;
  }
`;

export const TitleRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: 640px) {
    padding: 0;
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

export const Title = styled.h1`
  font-size: clamp(1.25rem, 4vw, 2rem);
  font-weight: 700;
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 0 ${({ theme }) => theme.spacing.xl};

  @media (max-width: 640px) {
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`;

export const IconButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  transition: ${({ theme }) => theme.transitions.button};

  &:hover {
    color: ${({ theme }) => theme.colors.errorDark};
  }
`;

export const VideoWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  max-height: 65vh;
  background: ${({ theme }) => theme.colors.darkBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;

  video { 
    width: 100%; 
    height: 100%; 
    min-height: 0; 
    object-fit: contain; 
  }

  @media (max-width: 640px) {
    max-height: none;
    aspect-ratio: auto;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    margin: 0 -${({ theme }) => theme.spacing.md};
    width: calc(100% + ${({ theme }) => theme.spacing.md * 2}px);
  }

  :fullscreen & { 
    max-height: 100dvh; 
    border-radius: 0;
    margin: 0;
    width: 100vw;
  }
`;

export const ScoreOverlay = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 20;
  pointer-events: none;

  @media (max-width: 640px) {
    position: static;
    margin: ${({ theme }) => theme.spacing.sm} auto 0;
    transform: scale(0.88);
    width: 100%;
    display: flex;
    justify-content: center;
  }

  :fullscreen & {
    position: fixed;
    top: 1rem;
    left: 1rem;
    transform: none;
    width: auto;
  }
`;

export const PlayerContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 400px;
  position: relative;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Video = styled.video`
  width: 100%;
  height: 100%;
  min-height: 400px;
  object-fit: contain;
  background: #000;
`;

export const ScoreboardSection = styled.div`
  width: 100%;
  margin: ${({ theme }) => theme.spacing.md} 0;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

export const ControlsPanel = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 700px;
  margin: ${({ theme }) => theme.spacing.md} auto;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.small};
  padding: ${({ theme }) => theme.spacing.md};
  transition: ${({ theme }) => theme.transitions.default};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: translateY(${({ visible }) => (visible ? 0 : '10px')});
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};

  @media (max-width: 640px) {
    position: sticky;
    bottom: 0;
    z-index: 30;
    width: 100%;
    max-width: none;
    margin: 0;
    padding: ${({ theme }) => theme.spacing.sm};
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(8px);
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    gap: ${({ theme }) => theme.spacing.xs};
    transform: none;

    > div {
      display: flex;
      gap: ${({ theme }) => theme.spacing.xs};
      flex-wrap: nowrap;
      justify-content: center;
      width: 100%;
      overflow-x: auto;
      padding: ${({ theme }) => theme.spacing.xs};
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }

      > div {
        display: flex;
        gap: ${({ theme }) => theme.spacing.xs};
        flex-wrap: nowrap;
        justify-content: center;
        min-width: min-content;
        padding: 0 ${({ theme }) => theme.spacing.xs};
      }
    }

    button {
      min-height: 44px;
      min-width: 90px;
      font-size: ${({ theme }) => theme.fontSizes.sm};
      flex: 1 0 90px;
      white-space: nowrap;
      background: ${({ theme }) => theme.colors.primary};
      color: white;
      border: none;
      border-radius: ${({ theme }) => theme.borderRadius.md};
      font-weight: 600;
      padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transition: all 0.2s ease;

      &:active {
        transform: translateY(1px);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }

      &:disabled {
        background: ${({ theme }) => theme.colors.gray};
        opacity: 0.7;
        cursor: not-allowed;
      }

      &[data-action="reset"] {
        background: ${({ theme }) => theme.colors.error};
        min-width: 80px;
      }
    }
  }
`;

export const Watermark = styled.div`
  position: absolute;
  top: 1rem;    /* Position at top instead of bottom */
  right: 1rem;  /* Position from right edge */
  background: rgba(255, 255, 255, 0.85);
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.15rem 0.3rem;
  z-index: 2;
  opacity: 0.7;
  pointer-events: none; /* Ensure it doesn't interfere with video controls */

  /* Adjust position in fullscreen mode */
  :fullscreen & {
    top: 1.5rem;
    right: 1.5rem;
  }

  /* Adjust for mobile screens */
  @media (max-width: 640px) {
    top: 0.75rem;
    right: 0.75rem;
  }
`;

export const WatermarkImg = styled.img`
  max-width: 35px;
  opacity: 0.85;
  display: block;
`;

export const SponsorsRow = styled.div`
  width: 100%;
  margin: ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  @media (max-width: 640px) {
    margin: ${({ theme }) => theme.spacing.sm} 0;
    padding: ${({ theme }) => theme.spacing.xs};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    transform: scale(0.9);
    transform-origin: top center;
  }
`;

export const SponsorsTitle = styled.h4`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: 500;

  @media (max-width: 640px) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
    margin-bottom: ${({ theme }) => theme.spacing.xs * 0.5};
  }
`;

export const SponsorsIcons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

export const SponsorItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs * 0.5};
  max-width: 72px;
  text-align: center;

  @media (max-width: 640px) {
    max-width: 60px;
  }
`;

export const SponsorLogo = styled.img`
  width: 34px;
  height: 34px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  object-fit: contain;
  background: white;
  padding: ${({ theme }) => theme.spacing.xs * 0.5};
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.2s ease;

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
    padding: ${({ theme }) => theme.spacing.xs * 0.25};
  }

  &:hover {
    transform: scale(1.05);
  }
`;

export const SponsorName = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.9;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 640px) {
    font-size: ${({ theme }) => theme.fontSizes.xs * 0.8};
  }
`;

export const StopButton = styled.button`
  margin-top: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.xl};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.button};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.errorDark};
  }
`;

export const InfoText = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} auto 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-align: center;
  line-height: 1.5;

  @media (max-width: 640px) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
    margin-top: ${({ theme }) => theme.spacing.xs};
  }
`;

export const Footer = styled.footer`
  width: 100%;
  text-align: left;
  margin-top: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textTertiary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  padding: 0 ${({ theme }) => theme.spacing.md};

  @media (max-width: 640px) {
    margin-top: ${({ theme }) => theme.spacing.md};
    padding: 0 ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.xs};
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const LoadingText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`;

export const ErrorMessage = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.error};
  background: rgba(255, 0, 0, 0.1);
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-align: center;
  margin: 0;
  width: 100%;
  max-width: 600px;
`;

export const RetryButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.button};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

export const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: #0056b3;
      &::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        padding: 8px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 1000;
        margin-bottom: 8px;
      }
    }
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }

  ${props => props.copied && css`
    background-color: #28a745;
    animation: pulse 0.5s ease;
  `}

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

export const MobileVideoContainer = styled.div`
  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${({ theme }) => theme.spacing.xs};
    padding-bottom: ${({ theme }) => theme.spacing.md};
    
    ${VideoWrap} {
      order: 1;
    }
    
    ${ScoreOverlay} {
      order: 2;
      margin-bottom: ${({ theme }) => theme.spacing.sm};
    }
    
    ${ControlsPanel} {
      order: 3;
      margin-top: auto;
    }
  }
`; 